import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  ClipboardCheck, Sparkles, Loader2, Trash2, ChevronDown, ChevronUp,
  Clock, Users, BarChart3, CheckCircle, XCircle, Plus, ArrowRight,
  Eye, X, Send, Link2, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_BANDS = [
  { value: "K", label: "Kindergarten" }, { value: "1", label: "Grade 1" },
  { value: "2", label: "Grade 2" }, { value: "3-4", label: "Grades 3-4" },
  { value: "5-6", label: "Grades 5-6" }, { value: "7-8", label: "Grades 7-8" },
  { value: "9-12", label: "Grades 9-12" },
];

const MODALITIES = [
  { value: "", label: "All Modalities" },
  { value: "listening", label: "Listening" },
  { value: "reading", label: "Reading" },
  { value: "speaking", label: "Speaking" },
  { value: "writing", label: "Writing" },
];

const WIDA_LEVELS = [
  { value: 1, label: "L1", name: "Entering", color: "bg-red-500" },
  { value: 2, label: "L2", name: "Emerging", color: "bg-orange-500" },
  { value: 3, label: "L3", name: "Developing", color: "bg-yellow-500" },
  { value: 4, label: "L4", name: "Expanding", color: "bg-blue-500" },
  { value: 5, label: "L5", name: "Bridging", color: "bg-indigo-500" },
  { value: 6, label: "L6", name: "Reaching", color: "bg-green-500" },
];

const QuickChecksTab = ({ classes = [] }) => {
  const [view, setView] = useState('list');
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [results, setResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);

  const [form, setForm] = useState({
    grade_level: '', modality: '', wida_levels: [3, 4],
    question_count: 6, title: '', class_id: '',
  });

  useEffect(() => { fetchChecks(); }, []);

  const fetchChecks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/quick-checks`);
      setChecks(res.data.checks || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const toggleLevel = (lvl) => {
    setForm(prev => {
      const levels = prev.wida_levels.includes(lvl)
        ? prev.wida_levels.filter(l => l !== lvl)
        : [...prev.wida_levels, lvl].sort();
      return { ...prev, wida_levels: levels.length > 0 ? levels : [lvl] };
    });
  };

  const handleCreate = async () => {
    if (!form.grade_level) { toast.error('Select a grade band'); return; }
    if (!form.wida_levels.length) { toast.error('Select at least one proficiency level'); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/quick-checks/create`, form);
      toast.success(`Quick Check created with ${res.data.question_count} questions`);
      fetchChecks();
      setView('list');
      setForm({ grade_level: '', modality: '', wida_levels: [3, 4], question_count: 6, title: '', class_id: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create quick check');
    } finally { setCreating(false); }
  };

  const handleDelete = async (checkId) => {
    if (!window.confirm('Delete this quick check and all submissions?')) return;
    try {
      await axios.delete(`${API}/quick-checks/${checkId}`);
      toast.success('Deleted');
      setChecks(prev => prev.filter(c => c.check_id !== checkId));
      if (selectedCheck?.check_id === checkId) { setSelectedCheck(null); setView('list'); }
    } catch { toast.error('Failed to delete'); }
  };

  const viewResults = async (check) => {
    setSelectedCheck(check);
    setLoadingResults(true);
    setView('results');
    try {
      const res = await axios.get(`${API}/quick-checks/${check.check_id}/results`);
      setResults(res.data);
    } catch (err) {
      toast.error('Failed to load results');
      setView('list');
    } finally { setLoadingResults(false); }
  };

  const copyLink = (checkId) => {
    const url = `${window.location.origin}/quick-check/${checkId}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link copied!')).catch(() => {
      window.prompt('Copy this link:', url);
    });
  };

  const toggleStatus = async (checkId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'closed' : 'active';
    try {
      await axios.patch(`${API}/quick-checks/${checkId}/status`, { status: newStatus });
      toast.success(newStatus === 'active' ? 'Quick check reopened' : 'Quick check closed');
      fetchChecks();
    } catch { toast.error('Failed to update status'); }
  };

  const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

  // ─── LIST VIEW ───
  if (view === 'list') {
    return (
      <div className="space-y-5" data-testid="quick-checks-list">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Formative Quick Checks</h2>
            <p className="text-xs text-gray-500">5-8 question mini-assessments from the MOSAIC question bank — auto-scored</p>
          </div>
          <Button onClick={() => setView('create')} className="bg-[#002FA7] hover:bg-[#002FA7]/90 text-white gap-1" data-testid="create-quick-check-btn">
            <Plus size={14} /> New Quick Check
          </Button>
        </div>

        {loading ? (
          <div className="py-12 text-center"><Loader2 className="animate-spin mx-auto text-[#002FA7]" size={24} /></div>
        ) : checks.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
            <ClipboardCheck size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No quick checks yet</p>
            <p className="text-xs text-gray-400 mt-1 mb-4">Create a short formative assessment in seconds</p>
            <Button onClick={() => setView('create')} variant="outline" className="gap-1" data-testid="empty-create-qc-btn">
              <Plus size={14} /> Create First Quick Check
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {checks.map(check => (
              <div key={check.check_id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all group" data-testid={`qc-card-${check.check_id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-800 text-sm truncate">{check.title}</h3>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${check.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {check.status === 'active' ? 'ACTIVE' : 'CLOSED'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{check.grade_level}</span>
                      <span className="text-[10px] font-bold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded capitalize">{check.modality}</span>
                      <span className="text-[10px] text-gray-400">{check.question_count} questions</span>
                      <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Users size={10} /> {check.submission_count || 0} submissions</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">{formatDate(check.created_at)}</p>
                  </div>
                  <div className="flex gap-1 items-center">
                    <button onClick={() => viewResults(check)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded" title="View Results" data-testid={`qc-results-${check.check_id}`}>
                      <BarChart3 size={14} />
                    </button>
                    <button onClick={() => copyLink(check.check_id)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded" title="Copy Student Link" data-testid={`qc-copy-${check.check_id}`}>
                      <Link2 size={14} />
                    </button>
                    <button onClick={() => toggleStatus(check.check_id, check.status)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded" title={check.status === 'active' ? 'Close' : 'Reopen'}>
                      {check.status === 'active' ? <X size={14} /> : <CheckCircle size={14} />}
                    </button>
                    <button onClick={() => handleDelete(check.check_id)} className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`qc-delete-${check.check_id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── CREATE VIEW ───
  if (view === 'create') {
    return (
      <div className="space-y-5" data-testid="quick-checks-create">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>New Quick Check</h2>
            <p className="text-xs text-gray-500">Select criteria — questions pulled automatically from the MOSAIC bank</p>
          </div>
          <Button onClick={() => setView('list')} variant="outline" size="sm">Back</Button>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-5">
          {/* Title */}
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Title (optional)</label>
            <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Auto-generated if left blank" className="h-10 border-gray-300" data-testid="qc-title" />
          </div>

          {/* Grade & Modality */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Grade Band *</label>
              <select value={form.grade_level} onChange={e => setForm(p => ({ ...p, grade_level: e.target.value }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="qc-grade">
                <option value="">Select...</option>
                {GRADE_BANDS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Modality</label>
              <select value={form.modality} onChange={e => setForm(p => ({ ...p, modality: e.target.value }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="qc-modality">
                {MODALITIES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>

          {/* Proficiency Levels */}
          <div>
            <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Target Proficiency Levels *</label>
            <div className="flex flex-wrap gap-2">
              {WIDA_LEVELS.map(lvl => {
                const sel = form.wida_levels.includes(lvl.value);
                return (
                  <button key={lvl.value} onClick={() => toggleLevel(lvl.value)} className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 transition-all ${sel ? `${lvl.color} text-white border-transparent` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'}`} data-testid={`qc-level-${lvl.value}`}>
                    {lvl.label} {lvl.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Question Count */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Number of Questions</label>
              <select value={form.question_count} onChange={e => setForm(p => ({ ...p, question_count: parseInt(e.target.value) }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="qc-count">
                {[3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>{n} questions</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase block mb-1.5">Assign to Class (optional)</label>
              <select value={form.class_id} onChange={e => setForm(p => ({ ...p, class_id: e.target.value }))} className="w-full h-10 text-sm border border-gray-300 rounded-md px-3" data-testid="qc-class">
                <option value="">No class assigned</option>
                {classes.map(c => <option key={c.class_id} value={c.class_id}>{c.name} ({c.grade_level})</option>)}
              </select>
            </div>
          </div>

          {/* Create Button */}
          <Button onClick={handleCreate} disabled={creating} className="w-full h-12 bg-[#002FA7] hover:bg-[#002FA7]/90 text-white text-base font-bold" data-testid="qc-create-btn">
            {creating ? (
              <><Loader2 size={18} className="animate-spin mr-2" /> Creating...</>
            ) : (
              <><ClipboardCheck size={18} className="mr-2" /> Create Quick Check</>
            )}
          </Button>

          <p className="text-[10px] text-gray-400 text-center">Questions are randomly selected from the MOSAIC bank matching your criteria. Multiple choice only — auto-scored instantly.</p>
        </div>
      </div>
    );
  }

  // ─── RESULTS VIEW ───
  if (view === 'results') {
    return (
      <div className="space-y-5" data-testid="quick-checks-results">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>{selectedCheck?.title || 'Results'}</h2>
            <p className="text-xs text-gray-500">Auto-scored formative assessment results</p>
          </div>
          <Button onClick={() => { setView('list'); setResults(null); }} variant="outline" size="sm">Back</Button>
        </div>

        {loadingResults ? (
          <div className="py-12 text-center"><Loader2 className="animate-spin mx-auto text-[#002FA7]" size={24} /></div>
        ) : results ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-[#002FA7]">{results.submission_count}</div>
                <div className="text-[10px] text-gray-500 uppercase font-bold">Submissions</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{results.class_average}%</div>
                <div className="text-[10px] text-gray-500 uppercase font-bold">Class Average</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-gray-700">{selectedCheck?.question_count || 0}</div>
                <div className="text-[10px] text-gray-500 uppercase font-bold">Questions</div>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                <div className={`text-2xl font-bold ${selectedCheck?.status === 'active' ? 'text-green-600' : 'text-gray-400'}`}>
                  {selectedCheck?.status === 'active' ? 'OPEN' : 'CLOSED'}
                </div>
                <div className="text-[10px] text-gray-500 uppercase font-bold">Status</div>
              </div>
            </div>

            {/* Per-Question Analysis */}
            {results.question_stats && results.question_stats.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2.5 border-b"><h3 className="text-xs font-bold text-gray-700">Question-by-Question Analysis</h3></div>
                <div className="divide-y divide-gray-100">
                  {results.question_stats.map((qs, i) => (
                    <div key={qs.question_id} className="px-4 py-3 flex items-center gap-3" data-testid={`qc-qstat-${i}`}>
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold ${qs.accuracy_pct >= 70 ? 'bg-green-100 text-green-700' : qs.accuracy_pct >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                        Q{i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-700 truncate">{qs.question_text}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] bg-gray-100 text-gray-600 px-1 rounded capitalize">{qs.modality}</span>
                          {qs.wida_level && <span className={`text-[10px] text-white px-1 rounded ${WIDA_LEVELS.find(w => w.value === qs.wida_level)?.color || 'bg-gray-400'}`}>L{qs.wida_level}</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className={`text-sm font-bold ${qs.accuracy_pct >= 70 ? 'text-green-600' : qs.accuracy_pct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{qs.accuracy_pct}%</div>
                        <div className="text-[10px] text-gray-400">{qs.correct}/{qs.answered}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Student Submissions */}
            {results.submissions && results.submissions.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2.5 border-b"><h3 className="text-xs font-bold text-gray-700">Student Submissions</h3></div>
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 text-[10px] text-gray-600 uppercase">
                    <th className="text-left p-3 font-bold">Student</th>
                    <th className="text-center p-3 font-bold">Score</th>
                    <th className="text-center p-3 font-bold">Correct</th>
                    <th className="text-right p-3 font-bold">Submitted</th>
                  </tr></thead>
                  <tbody>
                    {results.submissions.map((sub, i) => (
                      <tr key={i} className={`border-t border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`} data-testid={`qc-submission-${i}`}>
                        <td className="p-3 font-medium text-gray-800">{sub.student_name}</td>
                        <td className="p-3 text-center">
                          <span className={`font-bold ${sub.score_pct >= 70 ? 'text-green-600' : sub.score_pct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                            {sub.score_pct}%
                          </span>
                        </td>
                        <td className="p-3 text-center text-gray-600">{sub.correct}/{sub.total}</td>
                        <td className="p-3 text-right text-[10px] text-gray-400">{formatDate(sub.submitted_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {results.submission_count === 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-10 text-center">
                <Users size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500 text-sm">No submissions yet</p>
                <p className="text-[10px] text-gray-400 mt-1">Share the quick check link with students to collect responses</p>
                <Button onClick={() => copyLink(selectedCheck.check_id)} variant="outline" size="sm" className="mt-3 gap-1">
                  <Copy size={12} /> Copy Student Link
                </Button>
              </div>
            )}
          </>
        ) : null}
      </div>
    );
  }

  return null;
};

export default QuickChecksTab;
